import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import { TourPosition, TourService } from '../tour.service';
import {ButtonComponent} from '../../components/button/button.component';
import {LinkComponent} from '../../components/link/link.component';
import {IconsComponent} from '../../components/icons/icons.component';

type ArrowClass = 'arrow-top' | 'arrow-bottom' | 'arrow-left' | 'arrow-right';

const FALLBACK_POSITIONS: ConnectedPosition[] = [
  { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top',    offsetY: 14 },
  { originX: 'center', originY: 'top',    overlayX: 'center', overlayY: 'bottom', offsetY: -14 },
  { originX: 'end',    originY: 'center', overlayX: 'start',  overlayY: 'center', offsetX: 14 },
  { originX: 'start',  originY: 'center', overlayX: 'end',    overlayY: 'center', offsetX: -14 },
];

function buildPositions(on?: TourPosition): ConnectedPosition[] {
  if (!on) return FALLBACK_POSITIONS;

  const [side, align] = on.split('-');
  const h = (align === 'start' ? 'start' : align === 'end' ? 'end' : 'center') as 'start' | 'center' | 'end';
  const v = (align === 'start' ? 'top'   : align === 'end' ? 'bottom' : 'center') as 'top' | 'center' | 'bottom';

  let primary: ConnectedPosition;
  switch (side) {
    case 'top':    primary = { originX: h,       originY: 'top',    overlayX: h,       overlayY: 'bottom', offsetY: -14 }; break;
    case 'right':  primary = { originX: 'end',   originY: v,        overlayX: 'start', overlayY: v,        offsetX:  14 }; break;
    case 'left':   primary = { originX: 'start', originY: v,        overlayX: 'end',   overlayY: v,        offsetX: -14 }; break;
    default:       primary = { originX: h,       originY: 'bottom', overlayX: h,       overlayY: 'top',    offsetY:  14 }; break;
  }

  const fallbacks = FALLBACK_POSITIONS.filter(f =>
    !(f.originX === primary.originX && f.originY === primary.originY &&
      f.overlayX === primary.overlayX && f.overlayY === primary.overlayY),
  );
  return [primary, ...fallbacks];
}

@Component({
  selector: 'app-tour-popup',
  standalone: true,
  imports: [
    ButtonComponent,
    LinkComponent,
    IconsComponent
  ],
  templateUrl: './tour-popup.component.html',
  styleUrl: './tour-popup.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class TourPopupComponent implements AfterViewInit, OnDestroy {
  private popupTpl = viewChild.required('popupTpl', { read: TemplateRef });

  protected readonly tourService = inject(TourService);
  private readonly overlay = inject(Overlay);
  private readonly vcr = inject(ViewContainerRef);

  protected readonly arrowClass = signal<ArrowClass>('arrow-top');
  protected readonly closing  = signal(false);
  protected readonly centered = signal(false);

  /** Base Tailwind utilities for the popup card. Kept here so the template stays declarative. */
  private static readonly BASE_CLASSES =
    'relative max-w-xs min-w-60 rounded-lg p-6 bg-white dark:bg-gray-800 ' +
    'shadow-[0_4px_20px_rgba(0,0,0,0.12),0_1px_4px_rgba(0,0,0,0.08)] ' +
    'dark:shadow-[0_4px_20px_rgba(0,0,0,0.4),0_1px_4px_rgba(0,0,0,0.3)]';

  protected readonly popupClass = computed(() => {
    const step = this.tourService.currentStep();
    const parts = ['tour-popup', TourPopupComponent.BASE_CLASSES, this.arrowClass()];
    if (this.closing()) parts.push('tour-popup--closing');
    if (step?.arrow === false || this.centered()) parts.push('tour-popup--no-arrow');
    if (step?.classes) parts.push(step.classes);
    return parts.join(' ');
  });

  private portal: TemplatePortal<void> | null = null;
  private overlayRef: OverlayRef | null = null;
  private positionSub?: Subscription;
  private closeTimer?: ReturnType<typeof setTimeout>;
  private positionRaf?: number;
  private currentTargetEl: ElementRef | undefined;

  private pendingEl: ElementRef | undefined;
  private pendingOn: TourPosition | undefined;
  private hasPending = false;
  private viewReady = false;

  ngAfterViewInit() {
    this.portal = new TemplatePortal(this.popupTpl(), this.vcr);
    this.viewReady = true;
    if (this.hasPending) {
      this.hasPending = false;
      const el = this.pendingEl;
      const on = this.pendingOn;
      this.pendingEl = undefined;
      this.pendingOn = undefined;
      this.openOverlay(el, on);
    }
  }

  open(el: ElementRef | undefined, on?: TourPosition): void {
    if (!this.viewReady) {
      this.pendingEl = el;
      this.pendingOn = on;
      this.hasPending = true;
      return;
    }
    this.openOverlay(el, on);
  }

  close(): void {
    if (!this.overlayRef?.hasAttached()) return;
    clearTimeout(this.closeTimer);
    this.closing.set(true);
    this.closeTimer = setTimeout(() => {
      this.positionSub?.unsubscribe();
      this.positionSub = undefined;
      this.overlayRef?.dispose();
      this.overlayRef = null;
      this.closing.set(false);
    }, 200);
  }

  private openOverlay(el: ElementRef | undefined, on?: TourPosition): void {
    clearTimeout(this.closeTimer);
    cancelAnimationFrame(this.positionRaf!);
    this.currentTargetEl = el;

    const isCentered = !el;
    this.centered.set(isCentered);

    // When there is no target element use a global (viewport-centered) strategy;
    // otherwise anchor to the element with a flexible connected strategy.
    const connectedStrategy = isCentered ? undefined : this.overlay
      .position()
      .flexibleConnectedTo(el!.nativeElement)
      .withPositions(buildPositions(on))
      .withViewportMargin(12)
      .withFlexibleDimensions(false)
      .withPush(true);

    const strategy = connectedStrategy
      ?? this.overlay.position().global().centerHorizontally().centerVertically();

    if (this.overlayRef) {
      this.positionSub?.unsubscribe();
      this.positionSub = undefined;
      this.closing.set(false); // clear any in-progress close() animation

      const pane = this.overlayRef.overlayElement;
      const popup = pane.querySelector<HTMLElement>('.tour-popup');

      // Freeze and hide the popup synchronously before repositioning. The leave
      // animation can't be used here — it starts at opacity:1, so the popup would
      // always be visible at the new position for its full duration (the blink).
      // Instead we cut instantly, reposition while invisible, then enter-animate.
      // All three DOM writes land in the same frame so nothing is ever painted
      // at the new position before the fade-in begins.
      if (popup) {
        popup.classList.remove('tour-popup--closing');
        popup.style.animation = 'none';
        popup.style.opacity   = '0';
      }

      // Subscribe before updatePosition so the initial position emission is captured.
      if (connectedStrategy) {
        this.positionSub = connectedStrategy.positionChanges.subscribe(change => {
          const cls = this.getArrowClass(change.connectionPair);
          this.arrowClass.set(cls);
          requestAnimationFrame(() => this.updateArrowOffset(cls));
        });
      }
      this.overlayRef.updatePositionStrategy(strategy);
      this.overlayRef.updatePosition();

      this.positionRaf = requestAnimationFrame(() => {
        const p = pane.querySelector<HTMLElement>('.tour-popup');
        if (!p) return;
        p.style.opacity   = ''; // hand opacity back to the animation
        p.style.animation = 'none';
        p.getBoundingClientRect(); // force reflow so the browser registers the reset
        p.style.animation = '';   // tour-enter restarts from opacity:0
      });
    } else {
      const hasBackdrop = this.tourService.activeOptions().hasBackdrop ?? true;
      // usePopover: false — keeps the tour in z-index land so the custom
      // tour-overlay-container (z-index: 9999) reliably stacks above modals.
      this.overlayRef = this.overlay.create({
        positionStrategy: strategy,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        panelClass: 'tour-overlay-pane',
        hasBackdrop,
        backdropClass: hasBackdrop ? 'tour-backdrop' : undefined,
        usePopover: false,
      });
      // Move the overlay host into our dedicated container so the tour always
      // stacks above the default cdk-overlay-container (which hosts modals).
      TourPopupComponent.getOrCreateTourContainer().appendChild(this.overlayRef.hostElement);
      // Subscribe before attach so the initial position emission is captured.
      if (connectedStrategy) {
        this.positionSub = connectedStrategy.positionChanges.subscribe(change => {
          const cls = this.getArrowClass(change.connectionPair);
          this.arrowClass.set(cls);
          requestAnimationFrame(() => this.updateArrowOffset(cls));
        });
      }
      this.overlayRef.attach(this.portal!);
      // Enable position transitions one frame after the initial position is
      // applied, so the entry animation isn't a slide from 0,0.
      const ref = this.overlayRef;
      requestAnimationFrame(() => ref.overlayElement.classList.add('tour-overlay-pane--ready'));
    }
  }

  private static getOrCreateTourContainer(): HTMLElement {
    let container = document.querySelector<HTMLElement>('.tour-overlay-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'cdk-overlay-container tour-overlay-container';
      document.body.appendChild(container);
    }
    return container;
  }

  private getArrowClass(pair: ConnectedPosition): ArrowClass {
    if (pair.overlayY === 'top') return 'arrow-top';
    if (pair.overlayY === 'bottom') return 'arrow-bottom';
    if (pair.overlayX === 'start') return 'arrow-left';
    return 'arrow-right';
  }

  protected handleNext(): void {
    const onNext = this.tourService.currentStep()?.onNext;
    if (onNext) onNext(); else this.tourService.next();
  }

  protected handlePrev(): void {
    const onPrev = this.tourService.currentStep()?.onPrev;
    if (onPrev) onPrev(); else this.tourService.prev();
  }

  private updateArrowOffset(arrowClass: ArrowClass): void {
    if (!this.overlayRef) return;
    const popup = this.overlayRef.overlayElement.querySelector<HTMLElement>('.tour-popup');
    if (!popup) return;

    const target = this.currentTargetEl?.nativeElement;
    if (!target) return;

    const targetRect: DOMRect = target.getBoundingClientRect();
    const popupRect: DOMRect = popup.getBoundingClientRect();

    if (arrowClass === 'arrow-top' || arrowClass === 'arrow-bottom') {
      const center = targetRect.left + targetRect.width / 2 - popupRect.left;
      const clamped = Math.min(Math.max(center, 16), popupRect.width - 16);
      popup.style.setProperty('--tour-arrow-x', `${clamped}px`);
      popup.style.removeProperty('--tour-arrow-y');
    } else {
      const center = targetRect.top + targetRect.height / 2 - popupRect.top;
      const clamped = Math.min(Math.max(center, 16), popupRect.height - 16);
      popup.style.setProperty('--tour-arrow-y', `${clamped}px`);
      popup.style.removeProperty('--tour-arrow-x');
    }
  }

  ngOnDestroy() {
    clearTimeout(this.closeTimer);
    cancelAnimationFrame(this.positionRaf!);
    this.positionSub?.unsubscribe();
    this.overlayRef?.dispose();
  }
}
